"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import { usePathname, useRouter } from "next/navigation"
import {
  type AuthUser,
  clearTokens,
  getAccessToken,
  getRefreshToken,
  logoutApi,
  refreshTokenApi,
  requestOtpApi,
  setTokens,
  verifyOtpApi,
  verifyTokenApi,
} from "@/lib/auth"

// ===== Types =====

interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  requestOtp: (email: string) => Promise<{ detail: string }>
  verifyOtp: (email: string, code: string) => Promise<void>
  logout: () => Promise<void>
}

// ===== Context =====

const AuthContext = createContext<AuthContextValue | null>(null)

// ===== Provider =====

const PUBLIC_PATHS = ["/login"]

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const router = useRouter()
  const pathname = usePathname()

  const isAuthenticated = user !== null

  // Check stored tokens on mount
  useEffect(() => {
    let cancelled = false

    async function checkAuth() {
      try {
        const accessToken = getAccessToken()
        if (!accessToken) {
          // No access token — try refresh
          const refreshToken = getRefreshToken()
          if (refreshToken) {
            try {
              const tokens = await refreshTokenApi(refreshToken)
              setTokens(tokens.access, tokens.refresh)
              const verifiedUser = await verifyTokenApi(tokens.access)
              if (!cancelled) setUser(verifiedUser)
            } catch {
              clearTokens()
            }
          }
          return
        }

        // Verify access token
        try {
          const verifiedUser = await verifyTokenApi(accessToken)
          if (!cancelled) setUser(verifiedUser)
        } catch {
          // Token invalid — try refresh
          const refreshToken = getRefreshToken()
          if (refreshToken) {
            try {
              const tokens = await refreshTokenApi(refreshToken)
              setTokens(tokens.access, tokens.refresh)
              const verifiedUser = await verifyTokenApi(tokens.access)
              if (!cancelled) setUser(verifiedUser)
            } catch {
              clearTokens()
            }
          }
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    checkAuth()
    return () => { cancelled = true }
  }, [])

  // Redirect based on auth state
  useEffect(() => {
    if (isLoading) return

    const isPublicPath = PUBLIC_PATHS.some((p) => pathname.startsWith(p))

    if (!isAuthenticated && !isPublicPath) {
      router.replace("/login")
    } else if (isAuthenticated && pathname === "/login") {
      router.replace("/")
    }
  }, [isLoading, isAuthenticated, pathname, router])

  const requestOtp = useCallback((email: string) => requestOtpApi(email), [])

  const verifyOtp = useCallback(
    async (email: string, code: string) => {
      const data = await verifyOtpApi(email, code)
      setTokens(data.access, data.refresh)
      setUser(data.user)
      router.replace("/")
    },
    [router],
  )

  const logout = useCallback(async () => {
    const refresh = getRefreshToken()
    const access = getAccessToken()
    clearTokens()
    setUser(null)
    if (refresh) {
      await logoutApi(refresh, access)
    }
    router.replace("/login")
  }, [router])

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated, isLoading, requestOtp, verifyOtp, logout }),
    [user, isAuthenticated, isLoading, requestOtp, verifyOtp, logout],
  )

  const isPublicPath = PUBLIC_PATHS.some((p) => pathname.startsWith(p))

  // Show spinner while loading or while redirecting to login (prevents 401 flicker)
  if (isLoading || (!isAuthenticated && !isPublicPath)) {
    return (
      <AuthContext.Provider value={value}>
        <div className="h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </AuthContext.Provider>
    )
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// ===== Hook =====

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
